import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { selectData, selectById, selectIsLoading } from '../selectors.ts';
import { useAttach } from '../useAttach';

import { devicesStore, type DevicesStoreState } from './DevicesStore';

// TODO
// lets name this useDevicesInstances
export function useDevicesData() {
  useAttach(devicesStore, 'useDevicesData');

  return useStoreWithEqualityFn(devicesStore.store, selectData, shallow);
}
useDevicesData.store = devicesStore;

export function useDevicesById() {
  useAttach(devicesStore, 'useDevicesById');

  return useStoreWithEqualityFn(devicesStore.store, selectById, shallow);
}
useDevicesById.store = devicesStore;

export function useDevicesSelection({ deviceIds }: { deviceIds: string[] }) {
  useAttach(devicesStore, 'useDevicesSelection');

  const select = useCallback(
    (state: DevicesStoreState) => {
      type Device = NonNullable<DevicesStoreState['byId']>[string];

      const selection: { [key: string]: Device | null } = {};

      for (const deviceId of deviceIds) {
        selection[deviceId] = state.byId?.[deviceId] ?? null;
      }

      return selection;
    },
    [deviceIds],
  );

  return useStoreWithEqualityFn(devicesStore.store, select, shallow);
}
useDevicesSelection.store = devicesStore;

export function useDevice({ deviceId }: { deviceId: string }) {
  useAttach(devicesStore, 'useDevice');

  const select = useCallback(
    (state: DevicesStoreState) => {
      return {
        device: state.byId?.[deviceId] ?? null,
        deviceInstance: state.data?.[deviceId] ?? null,
        isLoading: state.isLoading,
        error: state.error,
      };
    },
    [deviceId],
  );

  return useStoreWithEqualityFn(devicesStore.store, select, shallow);
}
useDevice.store = devicesStore;

export function useDevicesLoading() {
  useAttach(devicesStore, 'useDevicesLoading');
  return useStoreWithEqualityFn(devicesStore.store, selectIsLoading, Object.is);
}
useDevicesLoading.store = devicesStore;

// TODO
// we can optimize this by making it partial devices that only update when relevant props change.
// something like use ordered device ids by zone id
export function useDevicesByZoneId() {
  useAttach(devicesStore, 'useDevicesByZoneId');

  const devices = useDevicesById();

  const byZoneId = useMemo(() => {
    if (devices.byId == null) return {};

    type Device = NonNullable<DevicesStoreState['byId']>[string];
    const result: Record<string, Record<string, Device>> = {};

    for (const device of Object.values(devices.byId)) {
      result[device.zone] = result[device.zone] ?? {};
      result[device.zone][device.id] = device;
    }

    return result;
  }, [devices.byId]);

  return {
    byZoneId: byZoneId,
    isLoading: devices.isLoading,
    error: devices.error,
  };
}
useDevicesByZoneId.store = devicesStore;

export function useDevicesAttach() {
  useAttach(devicesStore, 'useDevicesAttach');
}
useDevicesAttach.store = devicesStore;
